import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Bell, Settings, AlertTriangle, Info, RefreshCw, CheckCircle, X, Trash2, Eye, EyeOff, ArrowRight } from 'lucide-react'
import { useNotifications, Notification } from 'contexts/NotificationContext'
import { cn, formatNotificationDateTime } from 'lib/utils'
import { useTranslation } from 'react-i18next'
import { BatchDetailsModal } from 'features/candidates/components/BatchDetailsModal'

export function NotificationBell() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { notifications, unreadCount, removeNotification, markAsRead, markAllAsRead } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })
  const [isMobile, setIsMobile] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [showBatchDetails, setShowBatchDetails] = useState(false)
  const bellRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Calculate dropdown position
  useEffect(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below the bell
        right: window.innerWidth - rect.right // Align with right edge of bell
      })
    }
  }, [isOpen])

  // Handle click outside (desktop only - mobile uses backdrop)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const isClickInsideBell = bellRef.current?.contains(target)
      const isClickInsideDropdown = dropdownRef.current?.contains(target)
      
      if (!isMobile && isOpen && !isClickInsideBell && !isClickInsideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, isMobile])

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'system':
        return <Settings className="h-4 w-4 text-blue-500" />
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'update':
        return <RefreshCw className="h-4 w-4 text-green-500" />
      case 'reminder':
        return <Info className="h-4 w-4 text-yellow-500" />
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
    }
  }

  const getBorderColor = (type: Notification['type'], priority: Notification['priority']) => {
    const baseColors = {
      system: 'border-l-blue-500',
      alert: 'border-l-red-500',
      update: 'border-l-green-500',
      reminder: 'border-l-yellow-500',
      error: 'border-l-red-500'
    }
    
    const opacity = priority === 'high' ? '' : priority === 'medium' ? 'opacity-80' : 'opacity-60'
    return `${baseColors[type]} ${opacity}`
  }

  const getPriorityIndicator = (priority: Notification['priority']) => {
    if (priority === 'high') {
      return <div className="w-2 h-2 bg-red-500 rounded-full" />
    }
    return null
  }

  const translateCategory = (category?: string) => {
    if (!category) return null
    
    // Check if it's a predefined category key
    const categoryKey = (category && category.toLowerCase()) || ''
    const translationKey = `notifications.categories.${categoryKey}`
    const translated = t(translationKey)
    
    // If translation exists and is different from the key, use it
    return translated !== translationKey ? translated : category
  }

  const getTranslatedTitle = (notification: Notification) => {
    if (notification.title_key || notification.titleKey) {
      return t(notification.title_key || notification.titleKey || '')
    }
    return notification.title
  }

  const getTranslatedMessage = (notification: Notification): string => {
    if (notification.message_key || notification.messageKey) {
      // Normalize legacy keys saved in storage
      const normalizeKey = (key: string) => {
        if (key === 'common.validationErrors') return 'notifications.messages.validationErrors'
        return key
      }
      const messageKey = notification.message_key || notification.messageKey
      const messageParams = notification.message_params || notification.messageParams
      return t(normalizeKey(messageKey || ''), messageParams || {}) as string
    }
    return notification.message
  }

  return (
    <>
      {/* Full Page Dark Backdrop - Rendered at document body level (mobile only) */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9998] bg-black/30 transition-all duration-200 md:hidden"
          onClick={() => setIsOpen(false)}
        />,
        document.body
      )}
      
      <div className="relative">
        {/* Bell Icon */}
        <button
          ref={bellRef}
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <Bell className={cn(
            "h-5 w-5 transition-transform duration-200",
            isOpen && "rotate-12"
          )} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse shadow-lg">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {createPortal(
          <div 
            ref={dropdownRef}
            className={`fixed w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-[10000] max-h-96 overflow-y-auto transition-all duration-200 ease-out left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-auto ${
              isOpen 
                ? 'opacity-100 scale-100 translate-y-0' 
                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
            }`}
            style={{
              top: `${dropdownPosition.top}px`,
              ...(!isMobile ? { right: `${dropdownPosition.right}px` } : {})
            }}
          >
            {/* Arrow pointing to bell (hidden on mobile) */}
            <div className="hidden md:block absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
            
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('notifications.title')}
                </h3>
                {notifications.length > 0 && (
                  <div className="flex space-x-1">
                    <button
                      onClick={() => {
                        markAllAsRead()
                        setIsOpen(false)
                      }}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                      title={t('notifications.markAllRead')}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        notifications.forEach(n => removeNotification(n.id))
                        setIsOpen(false)
                      }}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title={t('notifications.clearAll')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <div className="relative">
                    <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full animate-ping"></div>
                    </div>
                  </div>
                  <p className="text-sm font-medium">{t('notifications.noNotifications')}</p>
                  <p className="text-xs mt-1 opacity-75">You're all caught up!</p>
                </div>
              ) : (
                notifications
                  .sort((a, b) => {
                    const timeA = a.timestamp?.getTime() || (a.created_at ? new Date(a.created_at).getTime() : 0)
                    const timeB = b.timestamp?.getTime() || (b.created_at ? new Date(b.created_at).getTime() : 0)
                    return timeB - timeA
                  })
                  .map((notification) => (
                    <div
                      key={notification.id}
                      className={cn(
                        "p-4 border-l-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer group",
                        getBorderColor(notification.type, notification.priority),
                        !notification.read && "bg-blue-50/50 dark:bg-blue-900/20"
                      )}
                      onClick={() => {
                        if (!notification.read) {
                          markAsRead(notification.id)
                        }
                        // If notification has batchId (from metadata or direct), show batch details modal
                        const batchId = notification.batchId || notification.metadata?.batchId || notification.metadata?.batch_id
                        if (batchId) {
                          setSelectedNotification(notification)
                          setShowBatchDetails(true)
                          setIsOpen(false)
                        }
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        {getIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <p className={cn(
                                  "text-sm font-medium",
                                  notification.read 
                                    ? "text-gray-700 dark:text-gray-300" 
                                    : "text-gray-900 dark:text-gray-100 font-semibold"
                                )}>
                                  {getTranslatedTitle(notification)}
                                </p>
                                {getPriorityIndicator(notification.priority)}
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                )}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {getTranslatedMessage(notification)}
                              </p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatNotificationDateTime(
                                    notification.timestamp || notification.created_at,
                                    i18n.language
                                  )}
                                  {notification.category && (
                                    <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
                                      {translateCategory(notification.category)}
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {!notification.read && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        markAsRead(notification.id)
                                      }}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-500 dark:hover:text-blue-400 dark:hover:bg-blue-900/20 rounded transition-colors"
                                      title="Mark as read"
                                    >
                                      <Eye className="h-3 w-3" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      removeNotification(notification.id)
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20 rounded transition-colors"
                                    title="Remove notification"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                              {notification.action && notification.action.onClick && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    notification.action?.onClick?.()
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 mt-2 font-medium"
                                >
                                  {notification.action.label}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>

            {/* Footer with View All link */}
            {notifications.length > 0 && (
              <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-2">
                <button
                  onClick={() => {
                    setIsOpen(false)
                    navigate('/notifications')
                  }}
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 py-1.5 px-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  {t('notifications.center.viewAll', 'View All Notifications')}
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Batch Details Modal */}
      <BatchDetailsModal
        isOpen={showBatchDetails}
        onClose={() => {
          setShowBatchDetails(false)
          setSelectedNotification(null)
        }}
        notification={selectedNotification}
      />
    </>
  )
}

